import {
  bitsToBase64Url,
  hasBit,
  newBits,
  setBit,
  type ShowcaseBeacon,
} from "@shared/showcase";

/**
 * Which seconds of the video this viewer saw, and which they came back to.
 *
 * Fed the playhead on every `timeupdate` (~4×/s). A second counts once per
 * PASS: arriving at second 12 marks it watched; leaving and arriving at it
 * again later (a seek back, a replay) marks it rewatched. Several updates
 * inside the same second are one arrival.
 *
 * Everything is cumulative, so the beacon is the whole picture each time and
 * the server's diff makes repeats harmless.
 */
export class WatchTracker {
  private watched: Uint8Array;
  private rewatched: Uint8Array;
  private lastSecond = -1;
  private furthest = 0;
  private dirty = false;

  constructor(durationSeconds: number | null) {
    this.watched = newBits(durationSeconds ?? 0);
    this.rewatched = newBits(durationSeconds ?? 0);
  }

  /** The playhead moved while PLAYING (not while scrubbing paused). */
  tick(currentTime: number): void {
    if (!Number.isFinite(currentTime) || currentTime < 0) return;
    const s = Math.floor(currentTime);
    this.furthest = Math.max(this.furthest, currentTime);
    if (s === this.lastSecond) return;
    if (hasBit(this.watched, s)) {
      if (!hasBit(this.rewatched, s)) {
        this.rewatched = setBit(this.rewatched, s);
        this.dirty = true;
      }
    } else {
      this.watched = setBit(this.watched, s);
      this.dirty = true;
    }
    this.lastSecond = s;
  }

  /** A seek or an end: the next tick is a new arrival even at the same second. */
  breakPass(): void {
    this.lastSecond = -1;
  }

  get hasNews(): boolean {
    return this.dirty;
  }

  beacon(sessionId: string): ShowcaseBeacon {
    this.dirty = false;
    return {
      sessionId,
      w: bitsToBase64Url(this.watched),
      r: bitsToBase64Url(this.rewatched),
      p: Math.floor(this.furthest),
    };
  }
}
